/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {info} from '@ion/core/src/logger/logger';
import {currentTheme, setCurrentTheme} from '@ion/dls/src/theme/ThemeStore';
import {DLSThemes} from '@ion/dls/src/theme/types';
import {FeatureFlag} from './FeatureFlags';

/**
 * Tracks whether the `dls` Feature Flag (`?features=dls`) was given when the
 * application loaded. It is held for the life of the page, so an in-app link
 * or a redirect that drops the query string keeps the New Theme Toggle; a
 * reload reads the query string again.
 */
export const newThemeToggleEnabled = signal<boolean>(false);

/**
 * Mutator function for enabling or disabling the New Theme Toggle.
 */
export const setNewThemeToggleEnabled = createMutator(newThemeToggleEnabled);

/**
 * Determines if the user may switch between the legacy and current themes -
 * only while the `dls` Feature Flag was given when the application loaded.
 * No role grants it; without it, neither theme's switch is shown and the
 * user stays on the legacy theme.
 *
 * ```typescript
 *
 *   import {hasNewThemeToggleAccess} from '@/security/themeAccess';
 *   ...
 *
 *   const canSwitchTheme = hasNewThemeToggleAccess();
 *
 * ```
 *
 * @returns `true` when the `dls` Feature Flag was given, otherwise `false`.
 */
export const hasNewThemeToggleAccess = (): boolean => newThemeToggleEnabled.value;

/**
 * Settles the New Theme Toggle and the theme from the query string's Feature
 * Flags, once, as the application loads - before its first render, so the
 * login page already shows the theme the user will have. With the `dls`
 * Feature Flag the application starts in the current theme, its toggle
 * shown; without it, in the legacy theme, with no toggle.
 *
 * ```typescript
 *
 *   import {initializeThemeAccess} from '@/security/themeAccess';
 *   ...
 *
 *   // https://ion.calamp.com/login?features=dls ...
 *   initializeThemeAccess(getFeatureFlags('features'));
 *
 * ```
 *
 * @param flags The Feature Flags given in the query string.
 */
export const initializeThemeAccess = (flags: string[]) => {
  const isEnabled = Boolean(flags?.includes(FeatureFlag.DLS));
  const theme     = isEnabled ? DLSThemes.current : DLSThemes.legacy;
  setNewThemeToggleEnabled(isEnabled);
  if (currentTheme.value !== theme) {
    setCurrentTheme(theme);
  }
};

/**
 * Holds the signed-in user to the legacy theme unless the `dls` Feature Flag
 * was given when the application loaded - should anything have moved to the
 * current theme before sign-in without it, the user is returned to the
 * legacy theme. Call it right after the user's features are initialized.
 *
 * ```typescript
 *
 *   import {verifyThemeAccess} from '@/security/themeAccess';
 *   ...
 *
 *   Features.initialize({...});
 *   verifyThemeAccess();
 *
 * ```
 *
 * @returns `true` when the user was returned to the legacy theme, otherwise
 *          `false`.
 */
export const verifyThemeAccess = (): boolean => {
  const isReturned = currentTheme.value === DLSThemes.current && !hasNewThemeToggleAccess();
  if (isReturned) {
    info('The current theme is not permitted without the dls feature flag - returning to the legacy theme');
    setCurrentTheme(DLSThemes.legacy);
  }
  return isReturned;
};

export default verifyThemeAccess;
